/**
 * AdminPayoutResolver.jsx
 *
 * Admin tool: attribute approved/confirmed payouts to a different GlobalUser.
 * Shows full fee breakdown (gross → platform fee → rail fee → net credited)
 * so admin can see exactly what amount hits the target user's balance.
 *
 * Fee logic mirrors approve_payout():
 *   role=Creator + interest_rate>0  → creator pays fee, net = gross - fee
 *   role=Client                     → client already paid fee on invoice, net = gross
 *   rail_fee_payer=creator          → additional rail deduction from net
 */

import React, { useState, useEffect } from "react";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import client from "../../../../API";

// ─────────────────────────────────────────────────────────────────────────────
// Formatters
// ─────────────────────────────────────────────────────────────────────────────

const fmt = (val) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val || 0);

const fmtDate = (val) => {
  if (!val) return "—";
  return new Date(val).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// FeeTag — small chip showing who absorbs the fee
// ─────────────────────────────────────────────────────────────────────────────

const FeeTag = ({ payout }) => {
  if (!payout) return null;
  const creatorPays = payout.fee_payer === "creator";
  return (
    <Tooltip
      title={
        creatorPays
          ? `Creator absorbs ${payout.platform_fee_pct}% platform fee${payout.rail_fee_pct > 0 ? ` + ${payout.rail_fee_pct}% ${payout.forced_payment_rail} rail fee` : ""}`
          : "Client already paid fee on invoice — creator receives full gross"
      }
    >
      <Chip
        size="small"
        icon={creatorPays ? <PersonIcon sx={{ fontSize: 12 }} /> : <BusinessIcon sx={{ fontSize: 12 }} />}
        label={creatorPays ? "Creator pays fee" : "Client pays fee"}
        color={creatorPays ? "warning" : "info"}
        variant="outlined"
        sx={{ fontSize: 11, height: 22 }}
      />
    </Tooltip>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FeeBreakdownCell — inline breakdown: gross → deductions → net
// ─────────────────────────────────────────────────────────────────────────────

const FeeBreakdownCell = ({ payout }) => {
  const gross        = parseFloat(payout.gross_amount || payout.amount || 0);
  const platformFee  = parseFloat(payout.platform_fee || 0);
  const railFee      = parseFloat(payout.rail_fee_amount || 0);
  const net          = parseFloat(payout.net_amount || gross);
  const creatorPays  = payout.fee_payer === "creator";

  if (!creatorPays) {
    // Client pays — simple display
    return (
      <Stack spacing={0}>
        <Typography variant="body2" fontWeight={700} color="success.main">
          {fmt(gross)}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          Client covers fees
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={0}>
      {/* Gross */}
      <Typography variant="caption" color="text.secondary" sx={{ textDecoration: "line-through" }}>
        {fmt(gross)} gross
      </Typography>
      {/* Deductions */}
      {platformFee > 0 && (
        <Typography variant="caption" color="error.main">
          − {fmt(platformFee)} ({payout.platform_fee_pct}% platform)
        </Typography>
      )}
      {railFee > 0 && (
        <Typography variant="caption" color="error.main">
          − {fmt(railFee)} ({payout.rail_fee_pct}% {payout.forced_payment_rail})
        </Typography>
      )}
      {/* Net */}
      <Typography variant="body2" fontWeight={700} color="success.main">
        {fmt(net)} net
      </Typography>
    </Stack>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FeeSummaryBar — aggregate totals above the table
// ─────────────────────────────────────────────────────────────────────────────

const FeeSummaryBar = ({ payouts, selectedIds }) => {
  const selected = payouts.filter((p) => selectedIds.includes(p.id));
  if (!selected.length) return null;

  const totals = selected.reduce(
    (acc, p) => ({
      gross:    acc.gross    + parseFloat(p.gross_amount || p.amount || 0),
      platform: acc.platform + parseFloat(p.platform_fee || 0),
      rail:     acc.rail     + parseFloat(p.rail_fee_amount || 0),
      net:      acc.net      + parseFloat(p.net_amount || p.amount || 0),
    }),
    { gross: 0, platform: 0, rail: 0, net: 0 }
  );

  const hasFees = totals.platform > 0 || totals.rail > 0;

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, mb: 1.5, bgcolor: "action.hover", borderColor: "primary.light" }}
    >
      <Stack direction="row" spacing={3} flexWrap="wrap">
        <Box>
          <Typography variant="caption" color="text.secondary">Selected</Typography>
          <Typography variant="body2" fontWeight={700}>{selected.length} payouts</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Gross total</Typography>
          <Typography variant="body2" fontWeight={700}>{fmt(totals.gross)}</Typography>
        </Box>
        {hasFees && (
          <>
            {totals.platform > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">Platform fees</Typography>
                <Typography variant="body2" fontWeight={700} color="error.main">
                  − {fmt(totals.platform)}
                </Typography>
              </Box>
            )}
            {totals.rail > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">Rail fees</Typography>
                <Typography variant="body2" fontWeight={700} color="error.main">
                  − {fmt(totals.rail)}
                </Typography>
              </Box>
            )}
          </>
        )}
        <Box>
          <Typography variant="caption" color="text.secondary">Net to credit</Typography>
          <Typography variant="body2" fontWeight={700} color="success.main">
            {fmt(totals.net)}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PayoutSearchPanel
// ─────────────────────────────────────────────────────────────────────────────

const PayoutSearchPanel = ({ selectedIds, onSelectionChange }) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [payouts, setPayouts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    client.payoutResolver
      .searchPayouts({
        page,
        rows: rowsPerPage,
        status: "approved,confirmed",
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      })
      .then((data) => { setPayouts(data.data || []); setTotal(data.total || 0); })
      .catch((e) => setError(e?.response?.data?.error || e.message || "Failed to load payouts"))
      .finally(() => setLoading(false));
  }, [debouncedSearch, page, rowsPerPage]);

  const handleToggle = (id) =>
    onSelectionChange(
      selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]
    );

  const allChecked = payouts.length > 0 && payouts.every((p) => selectedIds.includes(p.id));
  const someChecked = payouts.some((p) => selectedIds.includes(p.id)) && !allChecked;

  return (
    <Box>
      <TextField
        fullWidth size="small"
        placeholder="Search by creator ID, PO number, or creator name…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
          ),
        }}
        sx={{ mb: 1.5 }}
      />

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      {/* Fee summary for selected rows */}
      <FeeSummaryBar payouts={payouts} selectedIds={selectedIds} />

      <Paper variant="outlined">
        <TableContainer sx={{ maxHeight: 460 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={someChecked}
                    checked={allChecked}
                    disabled={loading || payouts.length === 0}
                    onChange={(e) =>
                      onSelectionChange(e.target.checked ? payouts.map((p) => p.id) : [])
                    }
                  />
                </TableCell>
                <TableCell>PO Number</TableCell>
                <TableCell>Creator</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Fee distribution</TableCell>
                <TableCell align="right">Amount credited</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : payouts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No approved / confirmed payouts found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                payouts.map((p) => (
                  <TableRow
                    key={p.id} hover selected={selectedIds.includes(p.id)}
                    onClick={() => handleToggle(p.id)} sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={selectedIds.includes(p.id)} />
                    </TableCell>

                    {/* PO Number */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                        {p.po_number}
                      </Typography>
                    </TableCell>

                    {/* Creator */}
                    <TableCell>
                      <Stack spacing={0}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                          {p.creator_display_name || p.creator_id}
                        </Typography>
                        {p.creator_display_name && p.creator_display_name !== p.creator_id && (
                          <Typography variant="caption" color="text.disabled">
                            {p.creator_id}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <Chip
                        label={p.status} size="small"
                        color={p.status === "approved" ? "info" : "warning"}
                        sx={{ textTransform: "capitalize", fontWeight: 600 }}
                      />
                    </TableCell>

                    {/* Fee distribution — who pays + breakdown */}
                    <TableCell>
                      <Stack spacing={0.5}>
                        <FeeTag payout={p} />
                        {p.forced_payment_rail && (
                          <Chip
                            size="small"
                            label={`${p.forced_payment_rail} rail`}
                            variant="outlined"
                            color={p.rail_fee_payer === "creator" ? "error" : "default"}
                            sx={{ fontSize: 10, height: 20 }}
                          />
                        )}
                      </Stack>
                    </TableCell>

                    {/* Amount breakdown */}
                    <TableCell align="right">
                      <FeeBreakdownCell payout={p} />
                    </TableCell>

                    {/* Date */}
                    <TableCell>
                      <Typography variant="caption">{fmtDate(p.payout_date)}</Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div" count={total} page={page}
          onPageChange={(_, v) => setPage(v)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TargetUserSelector — server-side autocomplete with current balance display
// ─────────────────────────────────────────────────────────────────────────────

const TargetUserSelector = ({ value, onChange }) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (inputValue.length < 2) { setOptions([]); return; }
    setLoading(true);
    client.payoutResolver
      .searchUsers({ search: inputValue, limit: 20 })
      .then((data) => setOptions(data.data || []))
      .catch(() => setOptions([]))
      .finally(() => setLoading(false));
  }, [inputValue]);

  return (
    <Autocomplete
      options={options} loading={loading} value={value}
      onChange={(_, newVal) => onChange(newVal)}
      inputValue={inputValue} onInputChange={(_, v) => setInputValue(v)}
      getOptionLabel={(opt) => `${opt.username} (${opt.email || "no email"})`}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      filterOptions={(x) => x}
      noOptionsText={inputValue.length < 2 ? "Type at least 2 characters…" : "No users found"}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Target account (search by username or email)"
          size="small"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <AccountBalanceWalletIcon fontSize="small" />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>{loading && <CircularProgress size={16} />}{params.InputProps.endAdornment}</>
            ),
          }}
        />
      )}
      renderOption={(props, opt) => (
        <li {...props} key={opt.id}>
          <Stack spacing={0} sx={{ width: "100%" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" fontWeight={600}>{opt.username}</Typography>
              <Typography variant="caption" color="success.main" fontWeight={700}>
                {fmt(opt.balance || 0)}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {opt.email || "no email"} · ID {opt.id}
              {opt.user_type ? ` · ${opt.user_type}` : ""}
            </Typography>
          </Stack>
        </li>
      )}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ResultsPanel — post-attribution summary with fee detail
// ─────────────────────────────────────────────────────────────────────────────

const ResultsPanel = ({ result }) => {
  const [open, setOpen] = useState(true);
  if (!result) return null;
  const successCount = result.success?.length || 0;
  const failCount    = result.failed?.length  || 0;
  const allFailed    = failCount > 0 && successCount === 0;

  return (
    <Paper variant="outlined" sx={{ borderColor: allFailed ? "error.main" : "success.main", mt: 2 }}>
      <Box
        sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 2, py: 1.5,
          bgcolor: allFailed ? "error.50" : "success.50",
          borderRadius: "4px 4px 0 0", cursor: "pointer",
        }}
        onClick={() => setOpen((x) => !x)}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {successCount > 0
            ? <CheckCircleIcon color="success" fontSize="small" />
            : <ErrorIcon color="error" fontSize="small" />}
          <Typography variant="subtitle2" fontWeight={700}>
            Resolution complete — {successCount} attributed, {failCount} failed
          </Typography>
          {result.total_attributed && parseFloat(result.total_attributed) > 0 && (
            <Chip
              label={`+${fmt(result.total_attributed)} net → ${result.target_username}`}
              color="success" size="small" sx={{ fontWeight: 700 }}
            />
          )}
          {result.final_balance && (
            <Chip
              label={`New balance: ${fmt(result.final_balance)}`}
              color="default" size="small" variant="outlined"
            />
          )}
        </Stack>
        <IconButton size="small">{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </Box>

      <Collapse in={open}>
        <Box sx={{ p: 2 }}>
          {result.success?.length > 0 && (
            <>
              <Typography variant="body2" fontWeight={600} color="success.main" gutterBottom>
                Attributed successfully
              </Typography>
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Payout ID</TableCell>
                    <TableCell>PO Number</TableCell>
                    <TableCell>Fee distribution</TableCell>
                    <TableCell align="right">Gross</TableCell>
                    <TableCell align="right">Platform fee</TableCell>
                    <TableCell align="right">Rail fee</TableCell>
                    <TableCell align="right">Net credited</TableCell>
                    <TableCell align="right">Balance after</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.success.map((row) => {
                    const gross   = parseFloat(row.gross_amount || row.amount || 0);
                    const pfee    = parseFloat(row.platform_fee || 0);
                    const rfee    = parseFloat(row.rail_fee || 0);
                    const net     = parseFloat(row.amount || 0);
                    const hasFees = pfee > 0 || rfee > 0;
                    return (
                      <TableRow key={row.payout_id}>
                        <TableCell>{row.payout_id}</TableCell>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                          {row.po_number}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            icon={row.fee_payer === "creator"
                              ? <PersonIcon sx={{ fontSize: 12 }} />
                              : <BusinessIcon sx={{ fontSize: 12 }} />}
                            label={row.fee_payer === "creator" ? "Creator" : "Client"}
                            color={row.fee_payer === "creator" ? "warning" : "info"}
                            variant="outlined"
                            sx={{ fontSize: 11, height: 22 }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ color: "text.secondary" }}>
                          {hasFees ? fmt(gross) : "—"}
                        </TableCell>
                        <TableCell align="right" sx={{ color: pfee > 0 ? "error.main" : "text.disabled" }}>
                          {pfee > 0 ? `− ${fmt(pfee)}` : "—"}
                        </TableCell>
                        <TableCell align="right" sx={{ color: rfee > 0 ? "error.main" : "text.disabled" }}>
                          {rfee > 0 ? `− ${fmt(rfee)}` : "—"}
                        </TableCell>
                        <TableCell align="right" sx={{ color: "success.main", fontWeight: 700 }}>
                          {fmt(net)}
                        </TableCell>
                        <TableCell align="right">
                          {fmt(row.balance_after)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </>
          )}

          {result.failed?.length > 0 && (
            <>
              <Typography variant="body2" fontWeight={600} color="error.main" gutterBottom>
                Failed
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Payout ID</TableCell>
                    <TableCell>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.failed.map((row) => (
                    <TableRow key={row.payout_id}>
                      <TableCell>{row.payout_id}</TableCell>
                      <TableCell sx={{ color: "error.main" }}>{row.error}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ConfirmDialog — shows net amount that will actually be credited
// ─────────────────────────────────────────────────────────────────────────────

const ConfirmDialog = ({ open, onClose, onConfirm, selectedPayouts, targetUser, reason, loading }) => {
  const totals = (selectedPayouts || []).reduce(
    (acc, p) => ({
      gross:    acc.gross    + parseFloat(p.gross_amount || p.amount || 0),
      platform: acc.platform + parseFloat(p.platform_fee || 0),
      rail:     acc.rail     + parseFloat(p.rail_fee_amount || 0),
      net:      acc.net      + parseFloat(p.net_amount || p.amount || 0),
    }),
    { gross: 0, platform: 0, rail: 0, net: 0 }
  );
  const hasFees = totals.platform > 0 || totals.rail > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Confirm Balance Attribution</DialogTitle>
      <DialogContent dividers>
        <DialogContentText component="div">
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="body2" color="text.secondary">Payouts to attribute</Typography>
              <Typography variant="h5" fontWeight={700}>{(selectedPayouts || []).length}</Typography>
            </Box>

            {/* Fee breakdown summary */}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack spacing={0.5}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Gross total</Typography>
                  <Typography variant="body2">{fmt(totals.gross)}</Typography>
                </Stack>
                {totals.platform > 0 && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="error.main">− Platform fees (creator pays)</Typography>
                    <Typography variant="body2" color="error.main">− {fmt(totals.platform)}</Typography>
                  </Stack>
                )}
                {totals.rail > 0 && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="error.main">− Rail fees (creator pays)</Typography>
                    <Typography variant="body2" color="error.main">− {fmt(totals.rail)}</Typography>
                  </Stack>
                )}
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" fontWeight={700} color="success.main">
                    Net credited to balance
                  </Typography>
                  <Typography variant="body2" fontWeight={700} color="success.main">
                    {fmt(totals.net)}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>

            <Box>
              <Typography variant="body2" color="text.secondary">Crediting account</Typography>
              <Typography variant="body1" fontWeight={600}>
                {targetUser?.username}{" "}
                <Typography component="span" variant="caption" color="text.secondary">
                  (ID {targetUser?.id})
                </Typography>
              </Typography>
              {targetUser?.email && (
                <Typography variant="caption" color="text.secondary">{targetUser.email}</Typography>
              )}
              {targetUser?.balance !== undefined && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Current balance: {fmt(targetUser.balance)} → after: {fmt(parseFloat(targetUser.balance || 0) + totals.net)}
                </Typography>
              )}
            </Box>

            {reason && (
              <Box>
                <Typography variant="body2" color="text.secondary">Admin note</Typography>
                <Typography variant="body2">{reason}</Typography>
              </Box>
            )}

            <Alert severity="warning" sx={{ mt: 1 }}>
              Each payout will be marked <strong>funded</strong> and the{" "}
              <strong>net amount</strong> (after creator-side fees) will be added to the
              target user's Blitz balance. This cannot be automatically undone.
            </Alert>
          </Stack>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={onConfirm} variant="contained" color="warning" disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <SwapHorizIcon />}
        >
          {loading ? "Processing…" : `Credit ${fmt(totals.net)} →`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Root — rendered as TabPanel index={2} inside UserBalancesView
// ─────────────────────────────────────────────────────────────────────────────

const AdminPayoutResolver = () => {
  // Track full payout objects (not just IDs) so we can compute fee totals
  const [selectedPayouts, setSelectedPayouts]   = useState([]);
  const [allVisiblePayouts, setAllVisiblePayouts] = useState([]);
  const [targetUser, setTargetUser] = useState(null);
  const [reason, setReason]         = useState("");
  const [confirmOpen, setConfirmOpen]   = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [result, setResult]             = useState(null);
  const [submitError, setSubmitError]   = useState(null);

  const selectedIds = selectedPayouts.map((p) => p.id);

  // PayoutSearchPanel gives us IDs; we resolve to full objects via allVisiblePayouts
  const handleSelectionChange = (newIds) => {
    // Keep payouts that are already selected + add newly selected from visible set
    const kept    = selectedPayouts.filter((p) => newIds.includes(p.id));
    const keptIds = new Set(kept.map((p) => p.id));
    const added   = allVisiblePayouts.filter((p) => newIds.includes(p.id) && !keptIds.has(p.id));
    setSelectedPayouts([...kept, ...added]);
  };

  const canSubmit = selectedPayouts.length > 0 && targetUser != null;

  const handleConfirm = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const data = await client.payoutResolver.resolve({
        target_global_user_id: targetUser.id,
        payout_ids: selectedIds,
        reason: reason || undefined,
      });
      setResult(data);
      setSelectedPayouts([]);
      setTargetUser(null);
      setReason("");
    } catch (e) {
      setSubmitError(e?.response?.data?.error || e.message || "Attribution failed");
    } finally {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  };

  // Net totals for action bar
  const netTotal = selectedPayouts.reduce(
    (sum, p) => sum + parseFloat(p.net_amount || p.amount || 0), 0
  );

  return (
    <Box>
      <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 3 }}>
        <AlertTitle sx={{ fontWeight: 700 }}>Orphaned Payout Resolution</AlertTitle>
        Use this when a creator was paid via BlitzPay but signed up under a different account.
        Select their <strong>approved / confirmed</strong> payouts, choose the correct destination
        account, and attribute the balance.{" "}
        <strong>Fee distribution is respected:</strong> if the payout was set to "creator pays fee,"
        only the net amount (after fee deduction) is credited — matching what the creator would have
        actually received. Each payout is marked <strong>funded</strong> with a full ledger audit trail.
      </Alert>

      <Stack spacing={3}>
        {/* Step 1 */}
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Step 1 — Find orphaned payouts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Only <strong>approved</strong> and <strong>confirmed</strong> payouts are shown.
            The <em>Fee distribution</em> column shows whether the creator or the client absorbs
            the platform/rail fees, and the <em>Amount credited</em> column shows the net that
            will land in the destination account.
          </Typography>

          {/* We wrap the panel in a hidden data-capture div to get access to full payout objects */}
          <_PayoutSearchPanelWithData
            selectedIds={selectedIds}
            onSelectionChange={handleSelectionChange}
            onPayoutsLoaded={setAllVisiblePayouts}
          />
        </Paper>

        {/* Step 2 */}
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Step 2 — Choose the destination account
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Search for the GlobalUser whose balance should be credited. Current balance is shown
            next to each result.
          </Typography>
          <Stack spacing={2}>
            <TargetUserSelector value={targetUser} onChange={setTargetUser} />
            {targetUser && (
              <Alert severity="success" sx={{ py: 0.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">
                    <strong>{targetUser.username}</strong> (ID {targetUser.id})
                    {targetUser.email ? ` · ${targetUser.email}` : ""}
                    {targetUser.user_type ? ` · ${targetUser.user_type}` : ""}
                  </Typography>
                  <Typography variant="body2" fontWeight={700} color="success.main">
                    Current balance: {fmt(targetUser.balance || 0)}
                  </Typography>
                </Stack>
              </Alert>
            )}
            <TextField
              label="Admin note (optional)" size="small" fullWidth multiline minRows={2}
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Creator created duplicate account; original payouts under @old_handle"
            />
          </Stack>
        </Paper>

        {/* Action bar */}
        <Paper
          variant="outlined"
          sx={{
            p: 2, display: "flex", alignItems: "center",
            justifyContent: "space-between", flexWrap: "wrap", gap: 2,
            bgcolor: canSubmit ? "action.hover" : "background.paper",
            borderColor: canSubmit ? "primary.main" : "divider",
          }}
        >
          <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">Payouts selected</Typography>
              <Typography variant="h6" fontWeight={700}>{selectedPayouts.length}</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="caption" color="text.secondary">Net to credit</Typography>
              <Typography variant="h6" fontWeight={700} color="success.main">
                {fmt(netTotal)}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="caption" color="text.secondary">Destination account</Typography>
              <Typography variant="h6" fontWeight={700}>
                {targetUser ? targetUser.username : <em style={{ opacity: 0.4 }}>not set</em>}
              </Typography>
            </Box>
            {targetUser && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="caption" color="text.secondary">Balance after</Typography>
                  <Typography variant="h6" fontWeight={700} color="success.main">
                    {fmt(parseFloat(targetUser.balance || 0) + netTotal)}
                  </Typography>
                </Box>
              </>
            )}
          </Stack>
          <Button
            variant="contained" color="warning" size="large"
            disabled={!canSubmit || submitting}
            startIcon={submitting ? <CircularProgress size={18} /> : <SwapHorizIcon />}
            onClick={() => setConfirmOpen(true)}
            sx={{ fontWeight: 700, minWidth: 220 }}
          >
            {canSubmit ? `Credit ${fmt(netTotal)} →` : "Attribute Balance →"}
          </Button>
        </Paper>

        {submitError && (
          <Alert severity="error">
            <AlertTitle>Attribution failed</AlertTitle>
            {submitError}
          </Alert>
        )}

        <ResultsPanel result={result} />
      </Stack>

      <ConfirmDialog
        open={confirmOpen} onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        selectedPayouts={selectedPayouts}
        targetUser={targetUser} reason={reason} loading={submitting}
      />
    </Box>
  );
};

// Internal version of PayoutSearchPanel that also surfaces the full payout objects
// to the parent via onPayoutsLoaded so fee totals can be computed.
const _PayoutSearchPanelWithData = ({ selectedIds, onSelectionChange, onPayoutsLoaded }) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [payouts, setPayouts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    client.payoutResolver
      .searchPayouts({
        page, rows: rowsPerPage,
        status: "approved,confirmed",
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      })
      .then((data) => {
        const rows = data.data || [];
        setPayouts(rows);
        setTotal(data.total || 0);
        onPayoutsLoaded(rows);
      })
      .catch((e) => setError(e?.response?.data?.error || e.message || "Failed to load payouts"))
      .finally(() => setLoading(false));
  }, [debouncedSearch, page, rowsPerPage]);

  const handleToggle = (id) => {
    const newIds = selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    onSelectionChange(newIds);
  };

  const allChecked = payouts.length > 0 && payouts.every((p) => selectedIds.includes(p.id));
  const someChecked = payouts.some((p) => selectedIds.includes(p.id)) && !allChecked;

  // Build fake payout objects for the fee summary (from what's visible)
  const visibleSelected = payouts.filter((p) => selectedIds.includes(p.id));

  return (
    <Box>
      <TextField
        fullWidth size="small"
        placeholder="Search by creator ID, PO number, or creator name…"
        value={search} onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
          ),
        }}
        sx={{ mb: 1.5 }}
      />
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      <FeeSummaryBar payouts={payouts} selectedIds={selectedIds} />

      <Paper variant="outlined">
        <TableContainer sx={{ maxHeight: 460 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={someChecked} checked={allChecked}
                    disabled={loading || payouts.length === 0}
                    onChange={(e) => onSelectionChange(e.target.checked ? payouts.map((p) => p.id) : [])}
                  />
                </TableCell>
                <TableCell>PO Number</TableCell>
                <TableCell>Creator</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Fee distribution</TableCell>
                <TableCell align="right">Amount credited</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : payouts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No approved / confirmed payouts found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                payouts.map((p) => (
                  <TableRow
                    key={p.id} hover selected={selectedIds.includes(p.id)}
                    onClick={() => handleToggle(p.id)} sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={selectedIds.includes(p.id)} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                        {p.po_number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                          {p.creator_display_name || p.creator_id}
                        </Typography>
                        {p.creator_display_name && p.creator_display_name !== p.creator_id && (
                          <Typography variant="caption" color="text.disabled">{p.creator_id}</Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={p.status} size="small"
                        color={p.status === "approved" ? "info" : "warning"}
                        sx={{ textTransform: "capitalize", fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <FeeTag payout={p} />
                        {p.forced_payment_rail && (
                          <Chip
                            size="small"
                            label={`${p.forced_payment_rail} rail${p.rail_fee_payer === "creator" ? " (creator)" : ""}`}
                            variant="outlined"
                            color={p.rail_fee_payer === "creator" ? "error" : "default"}
                            sx={{ fontSize: 10, height: 20 }}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <FeeBreakdownCell payout={p} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{fmtDate(p.payout_date)}</Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div" count={total} page={page}
          onPageChange={(_, v) => setPage(v)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default AdminPayoutResolver;